/**
 * Burton Forms — `<burton-subdivision-options>`. Extends `<burton-validate>`.
 *
 * Fills a wrapped `<select>`'s options with the SUBDIVISIONS of the current country, read from a
 * Shopify-rendered `country_option_tags` payload (the `data-provinces` attribute on each country
 * `<option>`). Shopify localizes those labels to the storefront locale, so nothing here — and no
 * locale file — has to carry state / province / prefecture names.
 *
 * A country with no subdivisions hides the whole element, which also drops it from validation.
 * The country always comes from `form.country`, so there is ONE code path whether the region has a
 * user-facing country picker (EU) or a fixed ambient country (US / CA / JP).
 *
 *   <burton-subdivision-options rules="required"
 *                               data-country-source="performer-country-source"
 *                               data-country-names="performer-country-names">
 *     <label for="subdivision">都道府県</label>
 *     <select id="subdivision" name="subdivision"><option value="">選択…</option></select>
 *   </burton-subdivision-options>
 */
/**
 * Subdivisions Burton actually ships to, keyed by ISO country code.
 * A country absent from this map is left unfiltered: Canada's legacy `statesCA` lists all 13
 * provinces and territories, and Japan ships to all 47 prefectures.
 */
const SHIPPABLE_SUBDIVISIONS = {
  US: [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ],
}

/**
 * @param {string|null} countryCode  ISO code, any case — `form.country`
 * @param {Array<[string, string]>} provinces  Shopify `[canonicalName, localizedLabel]` pairs
 * @returns {Array<[string, string]>}
 */
function filterToShippable (countryCode, provinces) {
  const allowed =
    SHIPPABLE_SUBDIVISIONS[String(countryCode || '').toUpperCase()]

  if (!allowed) return provinces

  return provinces.filter(
    ([canonicalName]) => allowed.indexOf(canonicalName) > -1,
  )
}

class BurtonSubdivisionOptions extends window.BAO.Forms.Validate {
  setupListeners () {
    super.setupListeners()

    customElements.whenDefined('burton-form').then(() => {
      this.subscribeToCountry()
      this.populate()
    })
  }

  subscribeToCountry () {
    const state = this.form && this.form.state

    if (!state || typeof state.subscribe !== 'function') {
      console.error(
        '[burton-subdivision-options] parent <burton-form> exposes no reactive state — the field will not repopulate when the country changes',
        this,
      )
      return
    }

    state.subscribe('country', () => this.populate())
  }

  populate () {
    const select = this.controls && this.controls[0]

    if (!select) return

    const subdivisions = this.subdivisions() || []
    const previousValue = select.value
    const placeholder = select.querySelector('option[value=""]')

    // Reset to the server-rendered (already-localized) placeholder, then rebuild.
    select.replaceChildren()

    if (placeholder) select.appendChild(placeholder)

    subdivisions.forEach(([canonicalName, localizedLabel]) => {
      const option = document.createElement('option')

      option.value = canonicalName
      option.textContent = localizedLabel
      select.appendChild(option)
    })

    // Keep a prior selection only if it survives the new country; else clear, so a stale
    // cross-country value can't be submitted.
    const stillValid = subdivisions.some(
      ([canonicalName]) => canonicalName === previousValue,
    )

    select.value = stillValid ? previousValue : ''

    // Countries with no subdivisions (most of the EU) hide the field.
    this.style.display = subdivisions.length > 0 ? '' : 'none'

    // Re-validate a previously-checked field so a stale cross-country value updates the coordinator's
    // isValid. The 'pending' guard skips the first populate (pre-interaction) so nothing is born invalid.
    if (this.validity && this.validity.state !== 'pending') this.validate()
  }

  /**
   * The current country's subdivisions, as Shopify's `[canonicalName, localizedLabel]` pairs.
   *
   * @returns {Array<[string, string]>|null} null when the country can't be resolved at all;
   *   an empty array when the country legitimately has no subdivisions.
   */
  subdivisions () {
    const sourceId = this.getAttribute('data-country-source')
    const source = sourceId ? document.getElementById(sourceId) : null

    if (!source) {
      console.error(
        `[burton-subdivision-options] no country source "${sourceId}" in the DOM — is country_option_tags rendered on this page?`,
      )
      return null
    }

    const countryName = this.countryName()

    // No country chosen yet — not an error, nothing to show.
    if (!countryName) return []

    const option = Array.from(source.options).find(
      (candidate) => candidate.textContent.trim() === countryName,
    )

    if (!option) {
      console.error(
        `[burton-subdivision-options] no country option labelled "${countryName}" in "${sourceId}" — either it's outside this store's shipping zones, or the name doesn't match Shopify's localized name`,
      )
      return null
    }

    const provinces = JSON.parse(option.dataset.provinces || '[]')

    return this.hasAttribute('data-shippable-only')
      ? filterToShippable(this.form && this.form.country, provinces)
      : provinces
  }

  /**
   * The LOCALIZED name of the country whose subdivisions we want.
   *
   * ONE path for every region: take `form.country` — the coordinator's single source of truth, an
   * ISO code, already reactive, already what the postal/phone rules key off — and translate it to a
   * localized name via the `data-country-names` map. That name is the join key into the
   * `country_option_tags` payload, which carries no ISO codes.
   *
   * @returns {string} '' when no country is set, or it isn't in the map
   */
  countryName () {
    const country = this.form && this.form.country

    if (!country) return ''

    const mapId = this.getAttribute('data-country-names')
    const mapEl = mapId ? document.getElementById(mapId) : null

    if (!mapEl) {
      console.error(
        `[burton-subdivision-options] no ISO→name map "${mapId}" in the DOM — cannot translate "${country}" into a payload lookup key`,
      )
      return ''
    }

    // form.country is lowercased when it comes from the <burton-form country> attribute and
    // uppercased when it comes from a picker's ISO value, so normalize. The map is emitted upcased.
    const names = JSON.parse(mapEl.textContent || '{}')
    const name = names[String(country).toUpperCase()]

    if (!name) {
      console.error(
        `[burton-subdivision-options] "${country}" is not in the ISO→name map — is it in this store's markets?`,
        names,
      )
      return ''
    }

    return name
  }
}

if (!customElements.get('burton-subdivision-options')) {
  customElements.define('burton-subdivision-options', BurtonSubdivisionOptions)
}

window.BAO.Forms.SubdivisionOptions =
  window.BAO.Forms.SubdivisionOptions || BurtonSubdivisionOptions
